import React from "react";
import { MapPin, ChevronRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { COURSE_DATA } from "./courseData";

const STATE_EMOJI = {
  WA:"🌲",CA:"🌉",TX:"⭐",FL:"🌴",NY:"🗽",OH:"✈️",GA:"🍑",CO:"🏔️",
  OR:"🌲",NC:"⭐",VA:"🏛️",PA:"🔔",AL:"🌲",AR:"🌾",AZ:"🌵",DE:"💎",
  ID:"🥔",IA:"🌽",KS:"🌾",KY:"🏇",LA:"🎷",MI:"🚗",MN:"❄️",MS:"🎵",
  MO:"🏛️",MT:"🦌",NE:"🌾",NV:"🎰",NJ:"🗽",NM:"🌶️",SC:"🌴",TN:"🎵",
  UT:"⛷️",WV:"⛏️",WI:"🧀",WY:"🦬",
};

export default function StateSelector({ onSelectState, isDark }) {
  const D = isDark;
  const states = Object.values(COURSE_DATA);

  const bg      = D ? "#0d1f3c" : "#f4f7fb";
  const panel   = D ? "#142a4d" : "#ffffff";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textSub = D ? "rgba(240,244,248,0.60)" : "#64748b";
  const gold    = "#c9971c";
  const goldL   = "#fbbf24";

  return (
    <div style={{ background: bg, minHeight: "60vh", padding: "clamp(24px,4vw,48px) clamp(16px,4vw,32px)" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "clamp(28px,4vw,48px)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(201,151,28,0.15)", border: "1px solid rgba(201,151,28,0.35)", borderRadius: 99, padding: "6px 16px", marginBottom: 16 }}>
            <ShieldCheck size={14} style={{ color: gold }} />
            <span style={{ fontSize: 11, fontWeight: 800, color: gold, letterSpacing: ".10em", textTransform: "uppercase" }}>PPCA · NPCA Exam Preparation</span>
          </div>
          <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: "clamp(26px,5vw,44px)", fontWeight: 900, color: text, margin: "0 0 12px", letterSpacing: "-.025em", lineHeight: 1.1 }}>
            National Certification Hub
          </h1>
          <p style={{ fontSize: "clamp(14px,2vw,17px)", color: textSub, maxWidth: 540, margin: "0 auto", lineHeight: 1.75 }}>
            Select your state to begin your crash course. Each module covers exact regulations, height triggers, equipment rules, and curfews tested on the official PPCA exam.
          </p>
        </div>

        {/* Info strip */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: "clamp(24px,3vw,36px)" }}>
          {[["🎯","94% First-Time Pass Rate","Among enrolled students"],
            ["📋","State-Specific Content","Not generic theory"],
            ["♾️","Lifetime Access","Study at your own pace"],
            ["🛡️","Pass Guarantee","We reset if you fail"]
          ].map(([ic,title,sub]) => (
            <div key={title} style={{ background: panel, borderRadius: 12, padding: "14px 16px", border: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{ic}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: text }}>{title}</div>
                <div style={{ fontSize: 11, color: textSub }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* State cards grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,320px),1fr))", gap: "clamp(12px,2vw,20px)" }}>
          {states.map((state) => {
            const emoji = STATE_EMOJI[state.id] || "📍";
            const done = state.progress === 100;
            const inProgress = state.progress > 0 && !done;

            return (
              <div key={state.id} onClick={() => onSelectState(state.id)}
                style={{
                  background: panel, borderRadius: 20, padding: "clamp(20px,3vw,28px)",
                  border: `1px solid ${border}`, cursor: "pointer",
                  boxShadow: D ? "0 2px 8px rgba(0,0,0,0.20)" : "0 2px 8px rgba(13,31,60,0.06)",
                  transition: "all .18s", display: "flex", flexDirection: "column", gap: 16,
                  position: "relative", overflow: "hidden",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.borderColor = gold; e.currentTarget.style.boxShadow = `0 8px 28px rgba(201,151,28,0.20)`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.borderColor = border; e.currentTarget.style.boxShadow = D ? "0 2px 8px rgba(0,0,0,0.20)" : "0 2px 8px rgba(13,31,60,0.06)"; }}
              >
                {/* Top row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ width: 54, height: 54, borderRadius: 14, background: D ? "rgba(201,151,28,0.15)" : "#fef9ec", border: `1.5px solid rgba(201,151,28,0.30)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>
                    {emoji}
                  </div>
                  {done ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(5,150,105,0.12)", border: "1px solid rgba(5,150,105,0.30)", borderRadius: 99, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#059669" }}>
                      <CheckCircle2 size={11} /> Certified
                    </span>
                  ) : inProgress ? (
                    <span style={{ background: D ? "rgba(201,151,28,0.15)" : "#fef9ec", border: "1px solid rgba(201,151,28,0.30)", borderRadius: 99, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: gold }}>
                      {state.progress}% Done
                    </span>
                  ) : null}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "clamp(16px,2.5vw,20px)", fontWeight: 900, color: text, marginBottom: 6, lineHeight: 1.2 }}>{state.title}</div>
                  <div style={{ fontSize: 13, color: textSub, lineHeight: 1.65, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{state.description}</div>
                </div>

                {/* Progress bar (if started) */}
                {state.progress > 0 && (
                  <div style={{ height: 4, background: D ? "rgba(255,255,255,0.08)" : "#e2e8f0", borderRadius: 99 }}>
                    <div style={{ height: "100%", width: `${state.progress}%`, background: `linear-gradient(90deg,${gold},${goldL})`, borderRadius: 99 }} />
                  </div>
                )}

                {/* Footer */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: `1px solid ${border}` }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: textSub }}>{state.modules.length} Modules + Exam Simulator</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 800, color: gold }}>
                    {done ? "Review" : inProgress ? "Continue" : "Start"} <ChevronRight size={14} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
